import React, { useEffect, useRef, useState } from 'react';

interface CollapsibleBoxProps {
    isOpen: boolean;
    durationMs?: number;
    onCloseComplete?: () => void;
    className?: string;
    children: React.ReactNode;
}

const CollapsibleBox: React.FC<CollapsibleBoxProps> = ({
    isOpen,
    durationMs = 300,
    onCloseComplete,
    className,
    children,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [maxHeight, setMaxHeight] = useState<string>('0px');
    const [overflowY, setOverflowY] = useState<'hidden' | 'auto'>('hidden');
    // const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }

        const handleTransitionEnd = (e: TransitionEvent) => {
            if (e.propertyName !== 'max-height') {
                return;
            }

            // setIsTransitioning(false);

            if (isOpen) {
                // Matches slideOpenTransition cleanup
                setOverflowY('auto');
                el.classList.add('open');
            } else {
                // Matches slideCloseTransition cleanup
                el.classList.remove('open');
                if (onCloseComplete) {
                    onCloseComplete();
                }
            }

            el.removeEventListener('transitionend', handleTransitionEnd);
        };

        // setIsTransitioning(true);
        setOverflowY('hidden');

        if (isOpen) {
            // ---- OPEN (equivalent to slideOpenTransition) ----
            const contentHeight = el.scrollHeight;
            setMaxHeight(`${contentHeight}px`);
        } else {
            // ---- CLOSE (equivalent to slideCloseTransition) ----
            const currentHeight = el.scrollHeight;
            setMaxHeight(`${currentHeight}px`);

            // Force layout so the browser registers the start height
            void el.offsetHeight;

            setMaxHeight('0px');
        }

        el.addEventListener('transitionend', handleTransitionEnd);
    }, [isOpen, onCloseComplete]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight,
                overflowY,
                transition: `max-height ${durationMs}ms ease`,
            }}
        >
            {children}
        </div>
    );
};

export default CollapsibleBox;
